import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import {
  RACK_DRAGGABLE_STATUSES,
  judgeRackDrop,
  PMS_BOARD_LABELS,
  PMS_BOARD_TITLES_FR,
  PMS_BOOKING_ORIGINS,
  PMS_BOOKING_ORIGIN_LABELS_FR,
  PMS_BOOKING_ORIGIN_LETTERS,
  PMS_STATUS_LABELS_FR,
  UPGRADE_KEEP_FREE,
  buildRoomFit,
  planRoomReshuffle,
  pmsBookingOrigin,
  pmsNightCount,
  proposeRoomAssignment,
  proposeUpgrades,
  journalLines,
  soldCategory,
  ok,
} from '@lemontaigu/pms-core';
import type {
  BedConfig,
  ChargeDay,
  Guest,
  JournalLine,
  Product,
  RackWindow,
  BookingSearchHit,
  BookingSearchResult,
  RatePlan,
  ReservationView,
  Result,
  Room,
  RoomStayView,
  RoomType,
} from '@lemontaigu/pms-core';
import { usePms, usePmsReach, type PmsBundle } from './PmsProvider.js';
import { ReservationForm } from './ReservationForm.js';
import { RackStripHead, RackStripRow } from './RackStrip.js';
import { RackInspector, type RackSelection, type RackTetris } from './RackInspector.js';
import { RackSearch } from './RackSearch.js';
import { CallFlow } from './call/CallFlow.js';
import { callButtonLabel } from './call/callModel.js';
import { rackStayMoney, type RackStayMoney } from './rackMoney.js';
import type { AdvanceRead } from './advanceView.js';
import type { PlanGapRead } from './planGapView.js';
import { billedLines, type BilledLine } from './billedLines.js';
import { mayLeave, useWarnBeforeUnload } from './leaveGuard.js';
import { dropConfirmFr, dropRefusalFr, dropStartFor } from './rackDropText.js';
import { rackPlanView, upgradeLineFr, type PlanNames, type RackGhost } from './rackPlan.js';
import { SEASON_COLORS, SEASON_SHORT, bandForDay, fiscalYearOf, type SeasonCal } from './seasonBands.js';
import {
  buildReservationWrite,
  formFromPlanCell,
  formFromReservation,
  withChargeDays,
  withPersons,
  type FormState,
} from './reservationDraft.js';
import {
  EMPTY_ROOM_CONFIG_FILTERS,
  RACK_OCCUPYING,
  ROOM_CONFIG_PRESETS,
  rackCovers,
  rackReadPeriod,
  type IsoPeriod,
  assignmentReasonsFr,
  assignmentRejectionsFr,
  bedRequestFr,
  bookingComment,
  fmtArrivalWindow,
  fmtDate,
  fmtDayCompact,
  filterRoomConfig,
  isWeekend,
  isoAddDays,
  isoRange,
  mergeBookingSearches,
  rackBar,
  rackBarFacts,
  rackMonthBands,
  rackRowFit,
  roomConfigOptions,
  roomConfigRows,
  sortRoomConfig,
  sortRoomConfigPreset,
  type RoomConfigFilters,
  type RoomConfigPreset,
  type RoomConfigRow,
  type RoomConfigSortKey,
  defaultPlanFor,
  propertyModes,
  rowIsVegaHeld,
} from './logic.js';

// « Plan d'occupation » — the per-unit rack (GLOSSARY: `rack`).
//
// The calendar answers « how many DSTD are free on Thursday »; this answers
// « is Apt2 free on Thursday ». One row per UNIT, stays drawn as bars.
//
// It exists because P1.6 step 6 needed a check the calendar cannot express: the
// Booking.com fidelity clause turned out unverifiable (the listing is closed),
// and the substitute — availability against the REAL bookings, per unit — has
// no instrument without this view.
//
// The geometry lives in `rackBar` as a pure function, tested at the edges that
// go quietly wrong: the departure night, the window boundaries, and back-to-back
// stays that must not collide.
//
// -- ONE BAR PER LOGEMENT (multi-room step 6c) ------------------------------
//
// It used to be one bar per RESERVATION, which was the same thing while a
// booking held one room and silently wrong the moment it held two: the booking
// carries min/max dates and its FIRST room's id (0090's derived columns), so a
// two-room booking would have drawn ONE bar, in ONE of its rooms, spanning
// BOTH stays -- showing the other room free while a guest is in it, and
// occupying nights in the first that nobody booked.
//
// So the bars come from `pms_room_stay` and the reservation is joined in only
// for the guest's name.
//
// -- DENSITY (2026-09-13) ---------------------------------------------------
//
// The target has always been the Vega view the desk reads today: the WHOLE
// hotel and a useful horizon on one screen, no scrolling in either axis. It was
// not being met, and the cause was one line of CSS rather than any decision
// here. `.pms-rack` carried `min-width: max-content` over `minmax(1.55rem, 1fr)`
// tracks, so under max-content sizing each BAR pushed its caption's width into
// the day columns it spans: « S.A.R.L. CADAY ROUGE MOTO TOUR » on a two-night
// stay set the width of every column in the grid. Measured at 1600x1000: day
// columns 209px instead of the 25px the rule asks for, the grid 5300px wide in
// a 1549px frame, SEVEN of 25 days and 24 of 42 units visible.
//
// The guest names were driving the calendar's scale. The fix is in the
// stylesheet (fixed-free tracks, `min-width: 0` on the bars); what changed HERE
// is the two things that bought the vertical half of it:
//
//   * the category BANDS are gone — six heading rows cost ~123px, about six
//     units. The category is a COLUMN in the strip instead, so sorting by it
//     reproduces the grouping as contiguous runs and costs nothing;
//   * the unit column became the Configuration glyph strip (`RackStrip`), which
//     is what the operator asked for and which the freed rows paid for.
//
// Measured after: 25 of 25 days, 41 of 42 units, no scrollbar on either axis.
//
// -- SORT AND FILTER --------------------------------------------------------
//
// Reused from `logic.ts` VERBATIM — the same `roomConfigRows` / `filterRoomConfig`
// / `sortRoomConfig` / `ROOM_CONFIG_PRESETS` that « Configuration hôtel » uses.
// Not similar functions: the same ones. Two screens that disagreed about what
// « Zone » order means would be a bug nobody notices until one of them is used
// to shut a wing down. `rackGroups` is no longer the ordering authority here and
// the default preset is « N° de chambre » (operator, 2026-09-13).
//
// -- WHAT A BAR SAYS (operator, 2026-09-30) ----------------------------------
//
//   * ORIGIN — a lettered tab at the bar's start (B · E · D · M · A, from
//     `pmsBookingOrigin`). Not a coloured contour, which was the first idea:
//     the fill already carries Vega's four status hues, and no five contour
//     colours stay distinct against all four of them. A letter reads on any
//     fill and to a colour-blind agent; the tab's colour is the quick cue.
//   * BOARD — the type de séjour (CH · PDJ · DP) right after the origin tab
//     (operator, 2026-10-18), from the stay's rate plan. The shared
//     `PMS_BOARD_LABELS` and the form's colour tokens, so the desk reads one
//     vocabulary across the form, Miroir Vega and here. On a one-night bar it
//     takes the room the name would have had: two letters of a surname say
//     less than the board does, and the tooltip still carries the whole name.
//   * FACTS — the requested bed, the arrival window and the comment, appended
//     after the name, in the tooltip, and in the inspector. The CSS cuts what
//     does not fit, so a narrow bar still shows the name first.
//
// -- ROW HEIGHT (operator, 2026-10-18) -----------------------------------------
//
// The rows were a fixed 1.2rem, sized for the reception desk's 1920x1200 and
// leaving a third of the operator's 4K screen blank. They now take the largest
// height that still fits the window (`rackRowFit`, measured below), floored at
// the old 1.2rem so the desk loses nothing, with the bar captions growing a
// little with the row. The wrapper's own max-height is the measured figure too,
// so a wrapped nav cannot push the last rows off the page.
//
// -- « TETRIS » v1 (operator, 2026-09-30) ------------------------------------
//
// Clicking a stay in « À attribuer » ranks the units it could go into
// (`proposeRoomAssignment`, pms-core) and ghosts the chosen one on its row. It
// is ADVISORY: until cutover a seeded booking's room comes from Vega every 30
// minutes, so the desk allocates in Vega and the rack catches up. There is
// deliberately no « Attribuer » button — one would write a room the next seed
// run puts back. Shifting already-allocated stays to dig out a slot (the part
// the staff call Tetris) is the full PMS's job, not v1's.
// Since 2026-10-29 (ADR-0028) a bar of a booking the PMS OWNS can be dragged onto a unit;
// a Vega-seeded bar still cannot, for exactly the reason above.
//
// -- A PERIOD, NOT THE TABLE (2026-09-30) -------------------------------------
//
// The rack loaded every stay and every booking. PostgREST stops a read at
// 1 000 rows with no error, the Vega seed never removes a checked-out stay, and
// production was at 877 and climbing — past the cap, the rows dropped would
// have been the furthest-future ones, drawn as free rooms. It now reads the
// window it shows through `rackWindow`, padded a week each side and widened to
// the whole of every stay in « À attribuer » (`rackReadPeriod`), and reads
// again whenever the window moves. Two rules keep that honest:
//   * a night the rack has not read is HATCHED, never drawn free;
//   * a proposal is only ranked for a stay whose every night was read — the
//     RPC's per-unit neighbours make the ranking exact inside the period, and
//     nothing outside it.
// Answers arriving for a window the desk has already left are dropped.
//
// -- « TETRIS » v2 (2026-10-01, docs/PMS-TETRIS-BRIEF.md) ------------------------
//
// When v1 finds no room free for the whole stay, `planRoomReshuffle` says which
// bookings to move to open one (at most three), or how to split the stay, or —
// in an overbooking — which rules it had to lift, in the operator's order, or
// how many rooms are missing. Each move can be vetoed (« ne pas déplacer ») and
// the plan is recomputed without it. The grid draws the whole plan: the target
// ghosted where it lands, each moved booking ghosted in its new room and its
// current bar drawn as leaving. « Surclassements de demain » is the day-before
// review (`proposeUpgrades`). All of it is advice for Vega until cutover.

/** How many nights the window spans. 7 is the default since 2026-09-30 (the
 *  operator: « number of days shown by default 7 days ») — the week the desk
 *  plays Tetris in, with bars wide enough to carry their facts. The others
 *  exist because « et à Noël ? » and « cette semaine » are different
 *  questions. Vega calls this « Nb de jours » and the desk already uses it. */
const DAY_CHOICES = [7, 15, 25, 35, 45] as const;

/** How many proposals the inspector lists before « + n autres ». */
const TETRIS_SHOWN = 5;

/** No stays yet — one array, so memos keyed on it do not recompute per render. */
const NO_STAYS: RoomStayView[] = [];

/** One établissement's reference lists (U3). Kept apart because two
 *  établissements' « DSTD » are two products and their bed vocabularies are
 *  their own: the planners must be given ONE établissement's, never a mix. */
interface RackRef {
  code: string | null;
  services: PmsBundle['services'];
  roomTypes: RoomType[];
  rooms: Room[];
  bedConfigs: BedConfig[];
  ratePlans: RatePlan[];
  products: Product[];
}

/** What the last completed read returned, and for which window it was made. */
interface RackRead {
  /** The window shown when the read was asked for — [windowFrom, windowTo). */
  windowFrom: string;
  windowTo: string;
  /** The period actually read — wider than the window (`rackReadPeriod`). */
  period: IsoPeriod;
  stays: RoomStayView[];
  reservations: ReservationView[];
}

export function Rack() {
  const pms = usePms();
  // Needs and allergens are health data (group edit step 5): read, shown and
  // sent only by a desk holding their own capability.
  const canNeeds = useCapability(CAPABILITIES.PMS_NEEDS_READ);
  const todayIso = useMemo(() => todayIsoParis(), []);
  const [startIso, setStartIso] = useState(todayIso);
  const [daysShown, setDaysShown] = useState<number>(7);
  const [read, setRead] = useState<RackRead | null>(null);
  const [reading, setReading] = useState(false);
  const [readErr, setReadErr] = useState('');
  // U3: one reference set per établissement in reach, and their union where a
  // list is all the grid needs (ids are unique across établissements).
  const [refs, setRefs] = useState<RackRef[]>([]);
  const roomTypes = useMemo(() => refs.flatMap((r) => r.roomTypes), [refs]);
  const rooms = useMemo(() => refs.flatMap((r) => r.rooms), [refs]);
  const bedConfigs = useMemo(() => refs.flatMap((r) => r.bedConfigs), [refs]);
  const ratePlans = useMemo(() => refs.flatMap((r) => r.ratePlans), [refs]);
  const products = useMemo(() => refs.flatMap((r) => r.products), [refs]);
  const [err, setErr] = useState('');
  // Order and filtering — the Configuration model exactly: a named preset OR a
  // column click, never both, so the header arrow and the active chip cannot
  // disagree about what is applied.
  const [preset, setPreset] = useState<RoomConfigPreset | null>('numero');
  const [sortKey, setSortKey] = useState<RoomConfigSortKey>('roomNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<RoomConfigFilters>(EMPTY_ROOM_CONFIG_FILTERS);
  // What the last click selected — rendered by the docked inspector. A click
  // NEVER reflows the grid: at 25 days across, losing your place on every click
  // is most of what would make the view unusable.
  const [picked, setPicked] = useState<
    { kind: 'stay'; id: string } | { kind: 'room'; id: number; date: string | null } | { kind: 'upgrades' } | null
  >(null);
  // The booking panel. An OVERLAY since the density work: rendered in the flow
  // it pushed the whole rack down, which is the same "the grid moved under me"
  // failure the inspector exists to avoid.
  const [form, setForm] = useState<FormState | null>(null);
  // U3: the établissement the open form belongs to — the booking's own, or
  // the unit a cell was clicked on; null for « Nouvelle », where the form asks.
  const [formProperty, setFormProperty] = useState<string | null>(null);
  // The open booking's advance payment (« Arrhes »), read with the rest of what
  // the form edits. A failed read does NOT stop the form opening: it is shown,
  // not edited, so a message stands in for the figure.
  const [formAdvance, setFormAdvance] = useState<AdvanceRead>(null);
  // Where Vega's charges and its rooming list disagree for the open booking (planGapView).
  const [formPlanGap, setFormPlanGap] = useState<PlanGapRead>(null);
  // The booking's whole-booking lines as billed (breakfast at 9,00 where the catalogue says 11,00).
  const [formBilled, setFormBilled] = useState<BilledLine[]>([]);
  // The open booking's « Journal » (O5): who did what, « par Marie D. ».
  const [formJournal, setFormJournal] = useState<JournalLine[]>([]);
  // Whether the desk has changed anything in the open form (the form reports it).
  // Leaving a dirty form — backdrop, Annuler, closing the tab — asks first; a
  // clean one closes without a question.
  const [formDirty, setFormDirty] = useState(false);
  // OB2 (Q1): the open booking is Vega's — the form opens read-only.
  const [formHeld, setFormHeld] = useState(false);
  // PB5: the établissement a phone call is being taken for — the « Appel » screen replaces the rack.
  const [callFor, setCallFor] = useState<string | null>(null);
  const reach = usePmsReach();
  // OB1: who holds a booking turns on its établissement's mode (Vega's until it is live).
  const modes = useMemo(() => propertyModes(reach.map((b) => b.property)), [reach]);
  // A booking is read and written through ITS établissement's services: the
  // screen's scoped ones would read nothing of another building's booking.
  const servicesFor = (code: string | null) => reach.find((b) => b.property.code === code)?.services ?? pms;
  const etablissementLabels = Object.fromEntries(reach.map((b) => [b.property.code, b.property.labelFr]));
  // What the rack reads from: every établissement in reach, each through its
  // own bundle — or, without a property list, the scoped services as before.
  const sources = useMemo(
    (): { code: string | null; services: PmsBundle['services'] }[] =>
      reach.length > 0 ? reach.map((b) => ({ code: b.property.code, services: b.services })) : [{ code: null, services: pms }],
    [reach, pms],
  );
  // Sections, the établissement filter, per-établissement planners: only with
  // several. With one the rack is exactly the single-building rack.
  const multi = reach.length > 1;
  const [saving, setSaving] = useState(false);
  // Which proposal the ghost shows — tied to the stay it was chosen for, so
  // picking another stay falls back to ITS best room rather than keeping this.
  const [preview, setPreview] = useState<{ stayId: string; roomId: number } | null>(null);
  // v2: which of a stay's plans is shown, and the « ne pas déplacer » the desk
  // has set — scoped to the stay (or to the review) they were set for, so
  // picking another one starts clean.
  const [planPick, setPlanPick] = useState<{ stayId: string; index: number } | null>(null);
  const [vetoes, setVetoes] = useState<{ scope: string; ids: string[] }>({ scope: '', ids: [] });
  const [upgradePreview, setUpgradePreview] = useState<string | null>(null);
  const ghostRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  /** The height the grid may have, in rem — measured, because it is not a
   *  constant: the nav wraps to a second line on a narrow window, the toolbar
   *  wraps, and the reception desk is not the operator's 4K screen. */
  const [availRem, setAvailRem] = useState<number | null>(null);

  const days = useMemo(() => isoRange(startIso, daysShown), [startIso, daysShown]);
  /** The window's end, EXCLUSIVE — the night of `windowTo` is not shown. */
  const windowTo = useMemo(() => isoAddDays(startIso, daysShown), [startIso, daysShown]);

  // The season calendars the window touches (one, or two across 1 December).
  // Read-only: a failure leaves the band off rather than blocking the rack.
  // Each établissement has its own calendar (U3): a band per section.
  const [seasonCalsBy, setSeasonCalsBy] = useState<Map<string | null, SeasonCal[]>>(() => new Map());
  const loadSeasons = useCallback(async () => {
    const years = new Set([fiscalYearOf(startIso), fiscalYearOf(isoAddDays(windowTo, -1))]);
    const per = await Promise.all(
      sources.map(async (src): Promise<[string | null, SeasonCal[]]> => {
        const list = await src.services.seasons.calendars();
        if (!list.ok) return [src.code, []];
        const wanted = list.value.filter((c) => years.has(c.year));
        const full = await Promise.all(wanted.map((c) => src.services.seasons.calendar(c.id)));
        return [src.code, full.flatMap((r) => (r.ok && r.value ? [r.value] : []))];
      }),
    );
    setSeasonCalsBy(new Map(per));
  }, [sources, startIso, windowTo]);
  // The single band of the one-building rack.
  const seasonCals = useMemo(() => (multi ? [] : [...seasonCalsBy.values()].flat()), [multi, seasonCalsBy]);

  // The units and the vocabularies — they do not move with the window.
  const loadReference = useCallback(async () => {
    const per = await Promise.all(
      sources.map(async (src): Promise<RackRef | string> => {
        const [types, rms, beds, plans, prods] = await Promise.all([
          src.services.reference.roomTypes(),
          src.services.reference.rooms(),
          src.services.reference.bedConfigs(),
          src.services.reference.ratePlans(),
          src.services.reference.products(),
        ]);
        const bad = [types, rms, beds, plans, prods].find((r) => !r.ok);
        if (bad && !bad.ok) return bad.error.message;
        return {
          code: src.code,
          services: src.services,
          roomTypes: types.ok ? types.value : [],
          rooms: rms.ok ? rms.value : [],
          bedConfigs: beds.ok ? beds.value : [],
          ratePlans: plans.ok ? plans.value : [],
          products: prods.ok ? prods.value : [],
        };
      }),
    );
    const failed = per.find((p): p is string => typeof p === 'string');
    if (failed !== undefined) {
      setErr(failed);
      return;
    }
    setRefs(per as RackRef[]);
  }, [sources]);

  /** The window of every établissement in reach, each read through its own
   *  bundle and joined (U3). One refusal refuses the read: a rack missing a
   *  building's stays would draw its units free. */
  const readWindow = useCallback(
    async (from: string, to: string): Promise<Result<RackWindow>> => {
      const per = await Promise.all(sources.map((src) => src.services.reservations.rackWindow(from, to)));
      const bad = per.find((r) => !r.ok);
      if (bad && !bad.ok) return bad;
      return ok({
        stays: per.flatMap((r) => (r.ok ? r.value.stays : [])),
        reservations: per.flatMap((r) => (r.ok ? r.value.reservations : [])),
      });
    },
    [sources],
  );

  // The stays and their bookings, for the window shown. Numbered, so an answer
  // for a window the desk has already paged away from is dropped rather than
  // drawn over the newer one.
  const readSeq = useRef(0);
  const loadWindow = useCallback(async () => {
    const seq = ++readSeq.current;
    setReading(true);
    setReadErr('');
    const period = rackReadPeriod(startIso, windowTo);
    const r = await readWindow(period.from, period.to);
    if (seq !== readSeq.current) return;
    if (!r.ok) {
      setReading(false);
      setReadErr(r.error.message);
      return;
    }
    // Drawn at once — it covers the window.
    setRead({ windowFrom: startIso, windowTo, period, ...r.value });
    // An unassigned stay reaching past the pad: read its whole stay too, or
    // its proposals would rank on nights nobody read. Until then that stay's
    // proposals wait (`pending`); the grid does not.
    const whole = rackReadPeriod(startIso, windowTo, r.value.stays);
    if (whole.from < period.from || whole.to > period.to) {
      const w = await readWindow(whole.from, whole.to);
      if (seq !== readSeq.current) return;
      if (!w.ok) {
        setReading(false);
        setReadErr(w.error.message);
        return;
      }
      setRead({ windowFrom: startIso, windowTo, period: whole, ...w.value });
    }
    setReading(false);
  }, [readWindow, startIso, windowTo]);

  useEffect(() => {
    void loadReference();
  }, [loadReference]);
  useEffect(() => {
    void loadWindow();
  }, [loadWindow]);
  useEffect(() => {
    void loadSeasons();
  }, [loadSeasons]);

  /** After a save: everything, as before the rack read by window. */
  const load = useCallback(async () => {
    setErr('');
    await Promise.all([loadReference(), loadWindow()]);
  }, [loadReference, loadWindow]);

  const reservations = read?.reservations ?? null;
  const stays = read?.stays ?? NO_STAYS;
  /** A night outside what was read — hatched, never drawn free. Everything is,
   *  until the first read lands. */
  const unread = (d: string) => read === null || d < read.period.from || d >= read.period.to;
  /** A read for THIS window is still to come: asked, or about to be (the
   *  effect runs after the render that moved the window). Not after a refusal
   *  — nothing is coming then, and the error says why. */
  const pending =
    readErr === '' &&
    (reading || read === null || read.windowFrom !== startIso || read.windowTo !== windowTo);

  // The booking each logement belongs to — for the guest's name, and nothing
  // else. Every date, status and unit on a bar comes from the stay itself.
  const bookingOf = useMemo(
    () => new Map((reservations ?? []).map((r) => [r.id, r])),
    [reservations],
  );
  const roomOf = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms]);

  // Only what actually holds a unit. A cancelled stay never occupied; a
  // checked-out one did, and keeps showing in a past window — hiding it would
  // make history look free. `listAllRoomStays` has already dropped RELEASED
  // logements, which hold nothing and must not draw.
  const occupying = useMemo(
    () => stays.filter((s) => RACK_OCCUPYING.includes(s.status)),
    [stays],
  );

  // ── The rows: Configuration's view-model, Configuration's sort and filter ──
  const allRows = useMemo(
    () => refs.flatMap((r) => roomConfigRows(r.rooms, r.roomTypes, r.bedConfigs)),
    [refs],
  );
  const options = useMemo(() => roomConfigOptions(allRows), [allRows]);
  const rows = useMemo(() => {
    const kept = filterRoomConfig(allRows, filters);
    return preset === null ? sortRoomConfig(kept, sortKey, sortDir) : sortRoomConfigPreset(kept, preset);
  }, [allRows, filters, preset, sortKey, sortDir]);
  const hidden = allRows.length - rows.length;

  // Unassigned stays occupy inventory too, so they get a band of their own
  // rather than being hidden — a rack that showed a free unit which is not
  // really free is precisely the error this view exists to catch.
  const unassigned = useMemo(
    () => occupying.filter((s) => s.roomId === null && rackBar(s, days) !== null),
    [occupying, days],
  );

  // U3: one section per établissement in reach, in their order (UD6) — each
  // with its own season band and its own « À attribuer ». The établissement
  // filter hides a section; the count below says so, as for any filter.
  const sections = useMemo(
    () =>
      multi
        ? reach
            .filter((b) => filters.etablissement === '' || filters.etablissement === b.property.code)
            .map((b) => ({
              code: b.property.code,
              label: b.property.labelFr,
              rows: rows.filter((r) => r.propertyCode === b.property.code),
              unassigned: unassigned.filter((x) => x.propertyCode === b.property.code),
              seasonCals: seasonCalsBy.get(b.property.code) ?? [],
            }))
            .filter((sec) => sec.rows.length > 0 || sec.unassigned.length > 0)
        : [],
    [multi, reach, filters.etablissement, rows, unassigned, seasonCalsBy],
  );

  /** The window shown is not yet read: its period is being fetched. */
  const windowUnread = !rackCovers(read?.period ?? null, startIso, windowTo) && readErr === '';

  // ── The row height (see `rackRowFit`) ──────────────────────────────────────
  // Measured from where the grid actually starts to the bottom of the window,
  // less the bottom padding and the fiscal-version footer (~3rem). The page
  // itself never scrolls for the grid: if the rows do not fit even at the floor
  // height, the grid's own wrapper scrolls, as it always did.
  //
  // A read in flight is skipped on purpose. « Lecture de la période… » joins the
  // toolbar for the length of the read and, on a narrow window, wraps it onto a
  // second line — pushing the grid down ~20px for a moment. The rows must not
  // follow that: they would shrink and grow with every page of the calendar.
  // The last steady figure stands until the read is over.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (wrap === null || windowUnread) return;
    const measure = () => {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const top = wrap.getBoundingClientRect().top + window.scrollY;
      const free = (window.innerHeight - top) / rem - 3;
      // No layout (a test renderer) reads as 0: keep the stylesheet's defaults.
      setAvailRem(free > 0 && top > 0 ? Math.floor(free * 100) / 100 : null);
    };
    measure();
    window.addEventListener('resize', measure);
    // Whatever is laid out ABOVE the grid — the nav (which wraps), the toolbar,
    // an error line — moves it when it changes height, and a window `resize`
    // does not always announce that.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer !== null) {
      for (let el: Element | null = wrap; el !== null && el !== document.body; el = el.parentElement) {
        for (let up = el.previousElementSibling; up !== null; up = up.previousElementSibling) observer.observe(up);
      }
    }
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [windowUnread]);

  const rowFit =
    availRem === null
      ? null
      : rackRowFit(
          availRem,
          rows.length +
            unassigned.length +
            (multi
              ? sections.reduce((n, sec) => n + 1 + (sec.seasonCals.length > 0 ? 1 : 0), 0)
              : seasonCals.length > 0
                ? 1
                : 0),
          unassigned.length > 0,
        );

  const barsFor = (roomId: number) =>
    occupying
      .filter((s) => s.roomId === roomId)
      .map((s) => ({ s, bar: rackBar(s, days) }))
      .filter((x): x is { s: RoomStayView; bar: NonNullable<ReturnType<typeof rackBar>> } => x.bar !== null);

  const guestOf = (s: RoomStayView) => bookingOf.get(s.reservationId)?.guest ?? null;

  /** The bar's caption: the OCCUPANT where one was named, else the GROUP's
   *  name for a group (PMS-GROUPS-BRIEF §6: the Planning shows the group on
   *  each room until a rooming list names the occupant), else the booker.
   *  On a two-room booking « DUPONT » twice tells the desk nothing about which
   *  room holds whom, which is the question the rack is being asked. */
  const groupOf = (s: RoomStayView) => {
    const r = bookingOf.get(s.reservationId);
    return r?.kind === 'group' ? r.groupName : null;
  };
  const barLabel = (s: RoomStayView) =>
    (s.occupantName ?? groupOf(s) ?? guestOf(s)?.lastName ?? '?').toUpperCase();

  const bedLabelOf = useMemo(() => {
    const m = new Map(bedConfigs.map((b) => [b.code, b.labelFr]));
    return (code: string) => m.get(code) ?? code;
  }, [bedConfigs]);

  // The rules v1, v2 and the review share (pms-core `roomFit`): the category
  // order (standing × size) and the bed layouts.
  const fit = useMemo(() => buildRoomFit(roomTypes, rooms, bedConfigs), [roomTypes, rooms, bedConfigs]);
  const fitBy = useMemo(
    () => new Map(refs.map((r) => [r.code, buildRoomFit(r.roomTypes, r.rooms, r.bedConfigs)])),
    [refs],
  );
  /** One établissement's units, occupancy and rules — all a planner is ever
   *  given (U3, rule 1: availability is never pooled, and neither is a
   *  proposal). With one établissement, everything, as before. */
  const partOf = (code: string | null) =>
    multi
      ? {
          rooms: rooms.filter((r) => r.propertyCode === code),
          occupying: occupying.filter((x) => x.propertyCode === code),
          fit: fitBy.get(code) ?? fit,
        }
      : { rooms, occupying, fit };
  const boardOf = useMemo(() => {
    const m = new Map(ratePlans.map((p) => [p.id, p.board]));
    return (id: number) => m.get(id) ?? null;
  }, [ratePlans]);

  const originOf = (s: RoomStayView) => {
    const r = bookingOf.get(s.reservationId);
    return r === undefined ? null : pmsBookingOrigin(r);
  };

  /** « Manuel — KRONCYCLING », « Autre — SITE MINDER »: the bucket, plus the
   *  raw channel where the bucket alone would hide which one it was. */
  const originText = (r: ReservationView) => {
    const o = pmsBookingOrigin(r);
    const label = PMS_BOOKING_ORIGIN_LABELS_FR[o];
    return (o === 'manual' || o === 'other') && r.channel ? `${label} — ${r.channel}` : label;
  };

  // The tooltip: the old one-liner, then one line per fact that is known.
  const title = (s: RoomStayView) => {
    const g = guestOf(s);
    const r = bookingOf.get(s.reservationId) ?? null;
    const who =
      s.occupantName === null
        ? `${(g?.lastName ?? '?').toUpperCase()} ${g?.firstName ?? ''}`
        : `${s.occupantName} (rés. ${(g?.lastName ?? '?').toUpperCase()})`;
    const lines = [
      `${who} · ${fmtDate(s.checkinDate)} → ${fmtDate(s.checkoutDate)} · ${pmsNightCount(
        s.checkinDate,
        s.checkoutDate,
      )} nuit(s) · ${PMS_STATUS_LABELS_FR[s.status]}`,
    ];
    const group = groupOf(s);
    if (group !== null) {
      const rooms = occupying.filter((x) => x.reservationId === s.reservationId).length;
      lines.push(`Groupe : ${group} (${rooms} logement${rooms > 1 ? 's' : ''} sur la période)`);
    }
    if (r !== null) lines.push(`Origine : ${originText(r)}`);
    if (r !== null && rowIsVegaHeld(modes, r)) lines.push('Tenue par Vega : son état se change dans Vega');
    const board = boardOf(s.ratePlanId);
    if (board !== null) lines.push(`Type de séjour : ${PMS_BOARD_TITLES_FR[board]}`);
    if (s.requestedBedConfig) lines.push(`Lit demandé : ${bedLabelOf(s.requestedBedConfig)}`);
    const arrival = fmtArrivalWindow(r?.arrivalWindowFrom, r?.arrivalWindowTo);
    if (arrival !== null) lines.push(`Arrivée prévue : ${arrival}`);
    const note = bookingComment({ notes: r?.notes ?? null });
    if (note !== null) lines.push(`Note : ${note}`);
    const said = bookingComment({ notes: null, sourceText: r?.sourceText });
    if (said !== null) lines.push(`Canal : ${said.length > 240 ? `${said.slice(0, 239)}…` : said}`);
    return lines.join('\n');
  };

  /** A bar's content: the origin tab, the board tag, the name, then the facts.
   *  The name is its own span so it stays findable whatever is appended to it. */
  const barBody = (s: RoomStayView) => {
    const origin = originOf(s);
    const board = boardOf(s.ratePlanId);
    const facts = rackBarFacts(s, bookingOf.get(s.reservationId) ?? null);
    return (
      <>
        {origin !== null && (
          <span className={`pms-rack-origin is-${origin}`} aria-hidden="true">
            {PMS_BOOKING_ORIGIN_LETTERS[origin]}
          </span>
        )}
        {board !== null && (
          // The type de séjour — the same codes and colours as the form and
          // Miroir Vega (group edit Q10): CH / PDJ / DP, never spelled out here.
          <span className={`pms-rack-board is-${board}`} aria-hidden="true">
            {PMS_BOARD_LABELS[board]}
          </span>
        )}
        {/* The text is its own truncating box, so that a one-night bar drops
            letters of the NAME and never the tags: with the ellipsis on the bar
            itself the browser removed a whole tag to make room for the « … ». */}
        <span className="pms-rack-text">
          <span className="pms-rack-name">{barLabel(s)}</span>
          {facts.length > 0 && <span className="pms-rack-facts">{facts.map((f) => ` · ${f}`).join('')}</span>}
        </span>
      </>
    );
  };

  const shift = (n: number) => setStartIso((s) => isoAddDays(s, n));

  // « Rechercher une réservation »: each établissement in reach answers through
  // its own services, and the answers are joined. A refusal from one is SAID —
  // dropping it would let « nothing found » stand for « you may not look ».
  const SEARCH_LIMIT = 20;
  const searchBookings = useCallback(
    async (query: string): Promise<Result<BookingSearchResult>> => {
      const parts = await Promise.all(sources.map((src) => src.services.reservations.searchBookings(query, SEARCH_LIMIT)));
      const values: BookingSearchResult[] = [];
      for (const p of parts) {
        if (!p.ok) return p;
        values.push(p.value);
      }
      return ok(mergeBookingSearches(values, todayIso, SEARCH_LIMIT));
    },
    [sources, todayIso],
  );
  /** Go to a found booking: the window opens a night before its arrival and its earliest
   *  logement is selected, the inspector listing the booking's others. A booking whose
   *  logements are all released has nothing to select in the grid: it opens as a form. */
  const goToBooking = async (hit: BookingSearchHit) => {
    setErr('');
    if (hit.stayId !== null) {
      setStartIso(isoAddDays(hit.checkinDate, -1));
      setPicked({ kind: 'stay', id: hit.stayId });
      return;
    }
    const got = await servicesFor(hit.propertyCode).reservations.get(hit.reservationId);
    if (!got.ok) {
      setErr(got.error.message);
      return;
    }
    if (got.value === null) {
      setErr('Réservation introuvable.');
      return;
    }
    await openEdit(got.value);
  };

  const toggleSort = (key: RoomConfigSortKey) => {
    if (key === sortKey && preset === null) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPreset(null); // a column click takes over from the preset
  };

  const setFilter = (patch: Partial<RoomConfigFilters>) => setFilters((f) => ({ ...f, ...patch }));

  // Opening an edit loads the stay's CHARGE LINES, not just the reservation.
  // Two reasons, and the second is the one that bites: the per-night prices are
  // only in the lines (the reservation carries a total), and `p_products` is
  // read by the writer as the COMPLETE set of extras — so a form that saved
  // without having loaded them would silently drop the guest's « animal ».
  // Since 6b the logements are loaded first and awaited — the form IS the room
  // list, so opening it before they arrive would show a booking with no rooms.
  const openEdit = async (r: ReservationView) => {
    // EVERYTHING the form edits is read before it opens: the logements, their
    // charge lines (the per-night prices, and the extras `p_products` must get
    // back complete), the persons, their meal exceptions and — for a desk
    // that may see them — their needs. The form takes its draft once — a read
    // landing after it opened would never reach it, and a save would then drop
    // what it had not loaded.
    const svc = servicesFor(r.propertyCode);
    const [stays, lines, persons, meals, needs, advance, gap, history] = await Promise.all([
      svc.reservations.roomStays(r.id),
      svc.reservations.chargeDays(r.id),
      svc.reservations.persons(r.id),
      svc.reservations.personMeals(r.id),
      canNeeds ? svc.reservations.personNeeds(r.id) : Promise.resolve(null),
      svc.arrivals.advance(r.id),
      svc.arrivals.planGap(r.id),
      svc.reservations.history(r.id),
    ]);
    for (const x of [stays, lines, persons, meals, needs]) {
      if (x !== null && !x.ok) {
        setErr(x.error.message);
        return;
      }
    }
    if (!stays.ok || !lines.ok || !persons.ok || !meals.ok || (needs !== null && !needs.ok)) return;
    setFormProperty(r.propertyCode);
    setFormAdvance(advance.ok ? advance.value : advance.error.message);
    setFormPlanGap(gap.ok ? gap.value : gap.error.message);
    setFormBilled(billedLines(lines.value, products));
    // A failed read only hides the journal: it is a reading, never edited.
    setFormJournal(history.ok ? journalLines(history.value) : []);
    setFormDirty(false);
    setFormHeld(rowIsVegaHeld(modes, r));
    setForm(
      withPersons(
        withChargeDays(formFromReservation(r, stays.value), lines.value, products),
        persons.value,
        meals.value,
        needs === null ? undefined : needs.value,
      ),
    );
  };

  // Saving is the SAME builder and the same services « Réservations » uses —
  // two screens that disagreed about what a booking is would be a bug nobody
  // notices until one renders the other's work wrongly.
  const save = async (f: FormState, propertyCode: string | null) => {
    const svc = servicesFor(propertyCode);
    const built = buildReservationWrite(f, defaultPlanFor(ratePlans, propertyCode));
    if (!built.ok) {
      setErr(built.error);
      return;
    }
    setSaving(true);
    setErr('');
    const { guest, fields, status } = built.write;
    const result =
      f.id === null
        ? await svc.reservations.book(guest, { ...fields, status, source: 'manual' })
        : await svc.reservations.update(f.id, guest, fields);
    setSaving(false);
    if (!result.ok) {
      setErr(result.error.message);
      return;
    }
    setForm(null);
    setFormDirty(false);
    await load();
  };

  /** Closes the open booking form — after asking, when the desk has typed
   *  something that closing would lose. */
  const closeForm = () => {
    if (!mayLeave(formDirty)) return;
    setForm(null);
    setFormDirty(false);
  };
  // Closing the tab or reloading with unsaved work asks too.
  useWarnBeforeUnload(form !== null && formDirty);
  // A press that STARTS inside the panel and ends on the backdrop (selecting
  // text, dragging a field) is not a click outside.
  const pressedBackdrop = useRef(false);

  // ── « Tetris » — proposals for a stay with no unit ────────────────────────
  //
  // Ranked in pms-core, worded in logic.ts, resolved here for the inspector.
  // `rooms` (every unit, not the filtered rows): a filter hides a unit from
  // the grid, never from the question « where can this guest go ».
  const tetrisFor = (stay: RoomStayView): RackTetris | { unavailable: string } => {
    // Every night of the stay must have been READ: `occupying` is only what
    // the last read returned, and a night outside it would look free.
    if (!rackCovers(read?.period ?? null, stay.checkinDate, stay.checkoutDate)) {
      if (readErr !== '') return { unavailable: 'Séjour non lu — voir l’erreur en haut du plan.' };
      if (pending) return { unavailable: 'Lecture du séjour complet…' };
      const inWindow = stay.checkinDate < windowTo && stay.checkoutDate > startIso;
      return {
        unavailable: inWindow
          ? `Séjour de ${pmsNightCount(stay.checkinDate, stay.checkoutDate)} nuits : plus long que ce que le plan lit d'un coup — pas de proposition.`
          : 'Séjour hors de la période affichée — revenez sur ses dates pour les propositions.',
      };
    }
    const part = partOf(stay.propertyCode);
    const { proposals, rejected } = proposeRoomAssignment(stay, part.rooms, part.occupying, todayIso, part.fit);
    const codeOf = new Map(roomTypes.map((t) => [t.id, t.code]));
    const standing = new Map(rooms.map((r) => [r.id, r.currentBedConfig]));
    const visible = new Set(rows.map((r) => r.id));
    const stayById = new Map(occupying.map((s) => [s.id, s]));
    const wanted = stay.requestedBedConfig ?? null;
    const lines = proposals.slice(0, TETRIS_SHOWN).map((p) => {
      const { reasons, warn } = assignmentReasonsFr(p, {
        categoryCode: p.roomTypeId === null ? null : (codeOf.get(p.roomTypeId) ?? null),
        nameOf: (id) => {
          const other = stayById.get(id);
          return other === undefined ? '?' : barLabel(other);
        },
        wanted,
        current: standing.get(p.roomId) ?? null,
      });
      return { roomId: p.roomId, roomNumber: p.roomNumber, reasons, warn, hidden: !visible.has(p.roomId) };
    });
    const chosen =
      preview !== null && preview.stayId === stay.id && lines.some((l) => l.roomId === preview.roomId)
        ? preview.roomId
        : (lines[0]?.roomId ?? null);
    // v2: nothing free throughout, so which bookings to move — or what gives.
    let plan: RackTetris['plan'] = null;
    if (proposals.length === 0) {
      const vetoed = vetoedFor(stay.id);
      const result = planRoomReshuffle(stay, part.rooms, part.occupying, todayIso, part.fit, {
        excluded: new Set(vetoed),
        isFullyRead,
        boardOf,
      });
      const names = planNames();
      const view = rackPlanView(result, stay.id, planPick?.stayId === stay.id ? planPick.index : 0, names);
      plan = {
        view,
        onNext: view.count > 1 ? () => setPlanPick({ stayId: stay.id, index: (view.index + 1) % view.count }) : null,
        onVeto: (id) => {
          setVetoes((v) => ({ scope: stay.id, ids: [...(v.scope === stay.id ? v.ids : []), id] }));
          setPlanPick(null);
        },
        vetoed: vetoed.map((id) => ({ stayId: id, name: names.stay(id)?.name ?? '?' })),
        onClearVetoes: () => setVetoes({ scope: '', ids: [] }),
      };
    }
    return {
      lines,
      more: Math.max(0, proposals.length - TETRIS_SHOWN),
      rejectedText: assignmentRejectionsFr(rejected, codeOf.get(soldCategory(stay)) ?? null, wanted),
      previewRoomId: chosen,
      onPreview: (roomId) => setPreview({ stayId: stay.id, roomId }),
      plan,
    };
  };

  /** Every night of a stay read — what v2 and the review require before they
   *  move it: its new room's other nights would otherwise be unknown. */
  const isFullyRead = (s: { checkinDate: string; checkoutDate: string }) =>
    rackCovers(read?.period ?? null, s.checkinDate, s.checkoutDate);

  const vetoedFor = (scope: string) => (vetoes.scope === scope ? vetoes.ids : []);

  /** How the plan wording names stays, rooms and categories. */
  const planNames = (): PlanNames => {
    const stayById = new Map(occupying.map((s) => [s.id, s]));
    const roomNo = new Map(rooms.map((r) => [r.id, r.roomNumber]));
    const typeCode = new Map(roomTypes.map((t) => [t.id, t.code]));
    return {
      stay: (id) => {
        const s = stayById.get(id);
        return s === undefined ? null : { name: barLabel(s), checkinDate: s.checkinDate, checkoutDate: s.checkoutDate };
      },
      room: (id) => roomNo.get(id) ?? '?',
      categoryOfRoom: (id) => {
        const t = rooms.find((r) => r.id === id)?.roomTypeId ?? null;
        return t === null ? null : (typeCode.get(t) ?? null);
      },
    };
  };

  // ── The selection, resolved for the inspector ─────────────────────────────
  //
  // Resolved here and handed down whole: the pane names no service and performs
  // no lookup (docs/ARCHITECTURE.md — the driver composes, the renderer draws).
  const resolved = useMemo((): { selection: RackSelection | null; ghosts: RackGhost[]; moving: string[] } => {
    const none = { ghosts: [] as RackGhost[], moving: [] as string[] };
    if (picked === null) return { selection: null, ...none };
    if (picked.kind === 'room') {
      const row = allRows.find((r) => r.id === picked.id);
      return { selection: row === undefined ? null : { kind: 'room', row, date: picked.date }, ...none };
    }
    if (picked.kind === 'upgrades') {
      // The day-before review: tomorrow's arrivals, against the read period.
      const date = isoAddDays(todayIso, 1);
      const vetoed = vetoedFor('upgrades');
      const names = planNames();
      // Per établissement, then joined: « keep N superior rooms free » is a
      // building's rule, and no guest is upgraded into another building.
      const proposals = (multi ? refs.map((r) => r.code) : [null]).flatMap((code) => {
        const part = partOf(code);
        return proposeUpgrades(date, part.rooms, part.occupying, part.fit, {
          keepFree: UPGRADE_KEEP_FREE,
          excluded: new Set(vetoed),
          isFullyRead,
          boardOf,
        });
      });
      const unread = occupying.filter((s) => s.checkinDate === date && !isFullyRead(s)).length;
      const note = !rackCovers(read?.period ?? null, date, isoAddDays(date, 1))
        ? 'Demain n’est pas dans la période lue — revenez à aujourd’hui.'
        : unread > 0
          ? `${unread} arrivée${unread > 1 ? 's' : ''} de demain non lue${unread > 1 ? 's' : ''} en entier, non examinée${unread > 1 ? 's' : ''}.`
          : null;
      const shown = proposals.find((u) => u.stayId === upgradePreview) ?? proposals[0];
      const who = shown === undefined ? undefined : occupying.find((s) => s.id === shown.stayId);
      return {
        selection: {
          kind: 'upgrades',
          review: {
            date,
            lines: proposals.map((u) => ({ stayId: u.stayId, text: upgradeLineFr(u, names), freeAfter: u.superiorFreeAfter })),
            note,
            keepFree: UPGRADE_KEEP_FREE,
            previewStayId: shown?.stayId ?? null,
            onPreview: (id) => setUpgradePreview(id),
            vetoed: vetoed.map((id) => ({ stayId: id, name: names.stay(id)?.name ?? '?' })),
            onVeto: (id) => setVetoes((v) => ({ scope: 'upgrades', ids: [...(v.scope === 'upgrades' ? v.ids : []), id] })),
            onClearVetoes: () => setVetoes({ scope: '', ids: [] }),
          },
        },
        ghosts:
          shown === undefined || who === undefined
            ? []
            : [{ roomId: shown.toRoomId, checkinDate: who.checkinDate, checkoutDate: who.checkoutDate, label: `${barLabel(who)} ↑`, kind: 'moved' }],
        moving: shown === undefined || shown.fromRoomId === null ? [] : [shown.stayId],
      };
    }
    const stay = occupying.find((s) => s.id === picked.id);
    if (stay === undefined) return { selection: null, ...none };
    const reservation = bookingOf.get(stay.reservationId) ?? null;
    // The assigned room's standing arrangement, so a Twin request in a room
    // made up as a Double is flagged where the desk is already looking.
    const inRoom = stay.roomId === null ? undefined : allRows.find((r) => r.id === stay.roomId);
    const tetris = stay.roomId === null ? tetrisFor(stay) : null;
    // What the grid draws: v1's previewed room, or v2's whole plan.
    let ghosts: RackGhost[] = [];
    let moving: string[] = [];
    if (tetris !== null && !('unavailable' in tetris)) {
      if (tetris.plan !== null) {
        ghosts = tetris.plan.view.ghosts;
        moving = tetris.plan.view.moving;
      } else if (tetris.previewRoomId !== null) {
        ghosts = [
          {
            roomId: tetris.previewRoomId,
            checkinDate: stay.checkinDate,
            checkoutDate: stay.checkoutDate,
            label: `${barLabel(stay)} ?`,
            kind: 'target',
          },
        ];
      }
    }
    const selection: RackSelection = {
      kind: 'stay',
      stay,
      reservation,
      siblings: stays.filter((s) => s.reservationId === stay.reservationId && s.id !== stay.id),
      origin: reservation === null ? null : originText(reservation),
      bed: bedRequestFr(
        stay.requestedBedConfig,
        bedLabelOf,
        stay.roomId === null ? undefined : (inRoom?.currentBedConfig ?? null),
      ),
      arrival: fmtArrivalWindow(reservation?.arrivalWindowFrom, reservation?.arrivalWindowTo),
      tetris,
    };
    return { selection, ghosts, moving };
    // tetrisFor, planNames and originText are plain closures over the state listed here.
  }, [
    picked,
    allRows,
    occupying,
    stays,
    bookingOf,
    bedLabelOf,
    rooms,
    roomTypes,
    rows,
    preview,
    todayIso,
    read,
    readErr,
    pending,
    startIso,
    windowTo,
    fit,
    fitBy,
    refs,
    multi,
    boardOf,
    planPick,
    vetoes,
    upgradePreview,
  ]);
  const selection = resolved.selection;
  const moving = useMemo(() => new Set(resolved.moving), [resolved.moving]);

  // The picked booking's charge lines — the pane's money comes from them, not
  // from `totalCents` (the whole booking). Read per pick: the rack window does
  // not carry lines, and a 42 x 25 grid's worth would be most of the folio
  // table. Keyed by the booking OBJECT so a reload after a save re-reads.
  const pickedBooking = selection?.kind === 'stay' ? selection.reservation : null;
  const [charges, setCharges] = useState<{ booking: ReservationView; lines: ChargeDay[] | string } | null>(null);
  useEffect(() => {
    if (pickedBooking === null) return;
    let cancelled = false;
    // Through the booking's own établissement (U3): the screen's scope would read
    // no line of another building's booking.
    const svc = reach.find((b) => b.property.code === pickedBooking.propertyCode)?.services ?? pms;
    void svc.reservations.chargeDays(pickedBooking.id).then((x) => {
      if (!cancelled) setCharges({ booking: pickedBooking, lines: x.ok ? x.value : x.error.message });
    });
    return () => {
      cancelled = true;
    };
  }, [pms, reach, pickedBooking]);
  // The picked booking's advance payment, read per pick like the charge lines
  // and keyed by the booking OBJECT for the same reason (a reload re-reads).
  const [advances, setAdvances] = useState<{ booking: ReservationView; read: AdvanceRead } | null>(null);
  useEffect(() => {
    if (pickedBooking === null) return;
    let cancelled = false;
    const svc = reach.find((b) => b.property.code === pickedBooking.propertyCode)?.services ?? pms;
    void svc.arrivals.advance(pickedBooking.id).then((x) => {
      if (!cancelled) setAdvances({ booking: pickedBooking, read: x.ok ? x.value : x.error.message });
    });
    return () => {
      cancelled = true;
    };
  }, [pms, reach, pickedBooking]);
  const advance: AdvanceRead =
    selection?.kind === 'stay' && advances !== null && advances.booking === selection.reservation ? advances.read : null;
  // …and whether Vega's two records of it disagree, read the same way.
  const [planGaps, setPlanGaps] = useState<{ booking: ReservationView; read: PlanGapRead } | null>(null);
  useEffect(() => {
    if (pickedBooking === null) return;
    let cancelled = false;
    const svc = reach.find((b) => b.property.code === pickedBooking.propertyCode)?.services ?? pms;
    void svc.arrivals.planGap(pickedBooking.id).then((x) => {
      if (!cancelled) setPlanGaps({ booking: pickedBooking, read: x.ok ? x.value : x.error.message });
    });
    return () => {
      cancelled = true;
    };
  }, [pms, reach, pickedBooking]);
  const planGap: PlanGapRead =
    selection?.kind === 'stay' && planGaps !== null && planGaps.booking === selection.reservation ? planGaps.read : null;
  const money = useMemo((): RackStayMoney | string | null => {
    if (selection?.kind !== 'stay' || charges === null || charges.booking !== selection.reservation) return null;
    if (typeof charges.lines === 'string') return charges.lines;
    return rackStayMoney(selection.stay, selection.siblings, charges.lines, boardOf);
  }, [selection, charges, boardOf]);

  // The ghosts: where the proposal being looked at puts each stay. The first is
  // scrolled into view on change — a proposal for room 402 is no use if row 402
  // is below the fold.
  const firstGhost = resolved.ghosts[0];
  useEffect(() => {
    ghostRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [firstGhost?.roomId, firstGhost?.checkinDate, firstGhost?.label]);

  /** What the inspector's primary button does — opening the booking form, in
   *  the one shape each selection can offer. Null where nothing can be opened,
   *  so the pane draws no button rather than a dead one. */
  const openFromSelection = (): (() => void) | null => {
    if (selection === null || selection.kind === 'upgrades') return null;
    if (selection.kind === 'stay') {
      const r = selection.reservation;
      return r === null ? null : () => void openEdit(r);
    }
    const { row, date } = selection;
    const room = roomOf.get(row.id);
    if (date === null || room === undefined) return null;
    return () => {
      setFormProperty(room.propertyCode);
      setFormAdvance(null);
      setFormPlanGap(null);
      setFormBilled([]);
      setFormJournal([]);
      setFormDirty(false);
      setFormHeld(false);
      setForm(formFromPlanCell(room, date));
    };
  };

  /** Double-click on a bar: the same form « Ouvrir la fiche » opens. A stay
   *  whose booking is not loaded opens nothing, as in the inspector. */
  const openStayById = (s: RoomStayView) => {
    const r = bookingOf.get(s.reservationId);
    if (r !== undefined) void openEdit(r);
  };

  // The grid's three kinds of row, as functions, so the one-building rack and
  // the sectioned one (U3) draw them identically.
  const seasonRow = (cals: SeasonCal[], key: string) =>
    cals.length === 0 ? null : (
      <div className="pms-rack-row is-season" aria-label="Saisons" key={key}>
        <span className="pms-rack-label is-muted">Saison</span>
        {days.map((d, i) => {
          const b = bandForDay(cals, d);
          return (
            <span
              key={d}
              className={`pms-season-cell ${b.closed ? 'is-closed' : ''}`}
              style={{
                gridColumn: i + 2,
                background: b.season ? SEASON_COLORS[b.season] : undefined,
              }}
              title={`${d} · ${b.title}`}
            >
              {b.season ? SEASON_SHORT[b.season] : ''}
            </span>
          );
        })}
      </div>
    );

  // ── Drag a bar onto a unit ───────────────────────────────────────────────────
  // The verdict is pms-core's `judgeRackDrop`; the words are `rackDropText`; the write is
  // `assignRoomStay`, whose RPC refuses what this cannot see. Only a booking the PMS owns can be
  // dragged: the Vega seed puts a seeded booking's room back within 30 minutes (ADR-0028).
  // A drop never changes the stay's dates — it says so when they would have.
  const dragRef = useRef<{ stayId: string; grabbedDaysIn: number } | null>(null);
  const [dropRoom, setDropRoom] = useState<number | null>(null);

  /** OB1's one predicate (`bookingIsVegaHeld`): a stay whose booking was not read counts as
   *  Vega's — « unknown » is not a licence to write — and a live établissement holds its own. */
  const isVegaOwned = (s: RoomStayView) => {
    const b = bookingOf.get(s.reservationId);
    return b === undefined || rowIsVegaHeld(modes, b);
  };

  const categoryName = (id: number) => {
    const t = roomTypes.find((x) => x.id === id);
    return t === undefined ? `catégorie ${id}` : `${t.labelFr} (${t.code})`;
  };

  const startDrag = (e: React.DragEvent<HTMLButtonElement>, s: RoomStayView, bar: { startIndex: number; span: number }) => {
    if (isVegaOwned(s)) {
      e.preventDefault();
      setErr(dropRefusalFr({ ok: false, reason: 'vega' }, ''));
      return;
    }
    if (!RACK_DRAGGABLE_STATUSES.includes(s.status)) {
      e.preventDefault();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dayWidth = rect.width / bar.span;
    const held = bar.startIndex + Math.min(bar.span - 1, Math.max(0, Math.floor((e.clientX - rect.left) / dayWidth)));
    const heldDay = days[held];
    dragRef.current = { stayId: s.id, grabbedDaysIn: heldDay === undefined ? 0 : pmsNightCount(s.checkinDate, heldDay) };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', s.id);
    setErr('');
  };

  const endDrag = () => {
    dragRef.current = null;
    setDropRoom(null);
  };

  const dropOn = async (e: React.DragEvent<HTMLElement>, roomId: number) => {
    e.preventDefault();
    const drag = dragRef.current;
    endDrag();
    const s = drag === null ? undefined : stays.find((x) => x.id === drag.stayId);
    const room = roomOf.get(roomId);
    if (drag === null || s === undefined || room === undefined) return;
    const cells = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('.pms-rack-cell'));
    const cell = cells.findIndex((c) => {
      const r = c.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX < r.right;
    });
    const verdict = judgeRackDrop({
      stay: {
        id: s.id,
        status: s.status,
        roomId: s.roomId,
        roomTypeId: s.roomTypeId,
        soldRoomTypeId: s.soldRoomTypeId ?? null,
        checkinDate: s.checkinDate,
        checkoutDate: s.checkoutDate,
        propertyCode: s.propertyCode,
        vegaOwned: isVegaOwned(s),
      },
      target: { roomId, roomTypeId: room.roomTypeId, propertyCode: room.propertyCode },
      others: occupying
        .filter((o) => o.roomId === roomId)
        .map((o) => ({ id: o.id, roomId: o.roomId, checkinDate: o.checkinDate, checkoutDate: o.checkoutDate })),
      dropStart: cell < 0 ? null : dropStartFor(days, cell, drag.grabbedDaysIn),
      fit: fitBy.get(s.propertyCode) ?? fitBy.get(null) ?? fit,
    });
    if (!verdict.ok) {
      setErr(dropRefusalFr(verdict, room.roomNumber));
      return;
    }
    setErr('');
    const text = dropConfirmFr(verdict.warnings, barLabel(s), room.roomNumber, categoryName);
    if (text !== null && !window.confirm(text)) return;
    const result = await servicesFor(s.propertyCode).reservations.assignRoomStay(
      s.id,
      roomId,
      verdict.changesCategory ? { allowOtherCategory: true } : undefined,
    );
    if (!result.ok) setErr(result.error.message);
    await load();
  };

  const unassignedBand = (list: RoomStayView[]) =>
    list.length === 0 ? null : (
      <>
        <div className="pms-rack-group">À attribuer</div>
        {list.map((s) => {
          const bar = rackBar(s, days)!;
          return (
            <div className="pms-rack-row" key={s.id}>
              <span className="pms-rack-label is-muted">—</span>
              {days.map((d, i) => (
                <span
                  key={d}
                  className={`pms-rack-cell ${isWeekend(d) ? 'is-weekend' : ''} ${d === todayIso ? 'is-today' : ''} ${
                    unread(d) ? 'is-unread' : ''
                  }`}
                  style={{ gridColumn: i + 2 }}
                />
              ))}
              <button
                type="button"
                className={`pms-rack-bar is-unassigned ${bar.clippedStart ? 'is-cut-start' : ''} ${
                  bar.clippedEnd ? 'is-cut-end' : ''
                } ${picked?.kind === 'stay' && picked.id === s.id ? 'is-picked' : ''}`}
                style={{ gridColumn: `${bar.startIndex + 2} / span ${bar.span}` }}
                title={title(s)}
                draggable
                onDragStart={(e) => startDrag(e, s, bar)}
                onDragEnd={endDrag}
                onClick={() => setPicked({ kind: 'stay', id: s.id })}
                onDoubleClick={() => openStayById(s)}
              >
                {barBody(s)}
              </button>
            </div>
          );
        })}
      </>
    );

  const unitRow = (row: RoomConfigRow) => (
    <div
      className={`pms-rack-row ${dropRoom === row.id ? 'is-drop' : ''}`}
      key={row.id}
      onDragOver={(e) => {
        if (dragRef.current === null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropRoom !== row.id) setDropRoom(row.id);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropRoom((r) => (r === row.id ? null : r));
      }}
      onDrop={(e) => void dropOn(e, row.id)}
    >
      <RackStripRow
        row={row}
        selected={picked?.kind === 'room' && picked.id === row.id}
        onSelect={() => setPicked({ kind: 'room', id: row.id, date: null })}
      />
      {days.map((d, i) => (
        <button
          key={d}
          type="button"
          className={`pms-rack-cell ${isWeekend(d) ? 'is-weekend' : ''} ${d === todayIso ? 'is-today' : ''} ${
            picked?.kind === 'room' && picked.id === row.id && picked.date === d ? 'is-picked' : ''
          } ${unread(d) ? 'is-unread' : ''}`}
          style={{ gridColumn: i + 2 }}
          // A free night in a unit: the click carries the unit AND the
          // date into the inspector, and its « Réserver » button opens
          // the form with both filled — operator L4's "nothing is
          // re-chosen" preserved, one step later. The step is the price
          // of the density: at 55px columns a click that threw a form
          // straight up would fire on every mis-aim.
          title={`${row.roomNumber} — ${fmtDate(d)}`}
          aria-label={`Réserver ${row.roomNumber} le ${fmtDate(d)}`}
          onClick={() => setPicked({ kind: 'room', id: row.id, date: d })}
          // Double-click = the booking form with unit and night filled
          // (the inspector's « Réserver » button, one click sooner).
          onDoubleClick={() => {
            const room = roomOf.get(row.id);
            if (room !== undefined) {
              setFormProperty(room.propertyCode);
              setFormAdvance(null);
              setFormPlanGap(null);
              setFormBilled([]);
              setFormJournal([]);
              setFormDirty(false);
              setFormHeld(false);
              setForm(formFromPlanCell(room, d));
            }
          }}
        />
      ))}
      {barsFor(row.id).map(({ s, bar }) => (
        <button
          key={s.id}
          type="button"
          className={`pms-rack-bar pms-rack-${s.status} ${bar.clippedStart ? 'is-cut-start' : ''} ${
            bar.clippedEnd ? 'is-cut-end' : ''
          } ${picked?.kind === 'stay' && picked.id === s.id ? 'is-picked' : ''} ${
            moving.has(s.id) ? 'is-moving' : ''
          }`}
          style={{ gridColumn: `${bar.startIndex + 2} / span ${bar.span}` }}
          title={title(s)}
          draggable
          onDragStart={(e) => startDrag(e, s, bar)}
          onDragEnd={endDrag}
          onClick={() => setPicked({ kind: 'stay', id: s.id })}
          onDoubleClick={() => openStayById(s)}
        >
          {barBody(s)}
        </button>
      ))}
      {resolved.ghosts
        .filter((g) => g.roomId === row.id)
        .map((g) => {
          const bar = rackBar(g, days);
          if (bar === null) return null;
          return (
            // Not a button and no title: it is a « what if », not a
            // stay, and nothing may find it as one.
            <span
              key={`${g.label}|${g.checkinDate}`}
              ref={g === firstGhost ? ghostRef : undefined}
              className={`pms-rack-ghost ${g.kind === 'moved' ? 'is-moved' : ''}`}
              style={{ gridColumn: `${bar.startIndex + 2} / span ${bar.span}` }}
              aria-hidden="true"
            >
              {g.label}
            </span>
          );
        })}
    </div>
  );

  if (callFor !== null) {
    return (
      <div className="pms">
        <CallFlow propertyCode={callFor} services={servicesFor(callFor)} onClose={() => setCallFor(null)} onSaved={() => void load()} />
      </div>
    );
  }

  return (
    <div
      className="pms pms-wide pms-rack-page"
      style={
        rowFit === null
          ? undefined
          : {
              ['--rack-avail' as string]: `${availRem}rem`,
              ['--rack-row-h' as string]: `${rowFit.rowRem}rem`,
              ['--rack-scale' as string]: String(rowFit.scale),
            }
      }
    >
      {/* One line, not a heading plus a hint plus a toolbar. Every row of page
          furniture above the grid is a row of units below it that the operator
          has to scroll for — 15rem of chrome was costing about seven. */}
      <div className="pms-rack-bar-top">
        <h2 className="section-title">Plan d'occupation</h2>

        <RackSearch search={searchBookings} onPick={(hit) => void goToBooking(hit)} propertyLabels={etablissementLabels} />

        {reach.map((b) => (
          <button key={b.property.code} type="button" className="auth-btn" onClick={() => setCallFor(b.property.code)}>
            {callButtonLabel(b.property)}
          </button>
        ))}

        <span className="pms-chips">
          <button type="button" className="pms-chip" onClick={() => shift(-7)} title="Reculer d'une semaine">
            «
          </button>
          <button type="button" className="pms-chip" onClick={() => shift(-1)} title="Reculer d'un jour">
            ‹
          </button>
          <button type="button" className="pms-chip" onClick={() => setStartIso(todayIso)}>
            Aujourd'hui
          </button>
          <button type="button" className="pms-chip" onClick={() => shift(1)} title="Avancer d'un jour">
            ›
          </button>
          <button type="button" className="pms-chip" onClick={() => shift(7)} title="Avancer d'une semaine">
            »
          </button>
        </span>

        {/* The day-before review (operator Q6): tomorrow's arrivals, upgraded
            while enough superior rooms stay free. */}
        <button
          type="button"
          className={`pms-chip ${picked?.kind === 'upgrades' ? 'is-on' : ''}`}
          title="Revue de la veille : les surclassements à faire pour les arrivées de demain"
          onClick={() => setPicked(picked?.kind === 'upgrades' ? null : { kind: 'upgrades' })}
        >
          Surclassements de demain
        </button>

        {/* Paging and a picker, not one or the other: stepping is how you scan
            a fortnight, jumping is how you answer « et à Noël ? » (operator D5). */}
        <label className="pms-rack-jump">
          Début
          <input type="date" value={startIso} onChange={(e) => e.target.value && setStartIso(e.target.value)} />
        </label>
        <label className="pms-rack-jump">
          Jours
          <select value={daysShown} onChange={(e) => setDaysShown(Number(e.target.value))}>
            {DAY_CHOICES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <span className="pms-rack-sep" />

        <span className="pms-cfg-sorts-label">Tri</span>
        {ROOM_CONFIG_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            title={p.title}
            className={`pms-chip ${preset === p.id ? 'is-on' : ''}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}

        {multi && (
          <label className="pms-rack-jump">
            Établissement
            <select value={filters.etablissement} onChange={(e) => setFilter({ etablissement: e.target.value })}>
              <option value="">Tous</option>
              {reach.map((b) => (
                <option key={b.property.code} value={b.property.code}>
                  {b.property.labelFr}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="pms-rack-jump">
          Type
          <select value={filters.bedConfig} onChange={(e) => setFilter({ bedConfig: e.target.value })}>
            <option value="">Tous</option>
            {options.bedConfigs.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-rack-jump">
          Catégorie
          <select value={filters.category} onChange={(e) => setFilter({ category: e.target.value })}>
            <option value="">Toutes</option>
            {options.categories.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-rack-jump">
          Zone
          <select value={filters.heatingZone} onChange={(e) => setFilter({ heatingZone: e.target.value })}>
            <option value="">Toutes</option>
            {options.heatingZones.map((v) => (
              <option key={v} value={v}>
                Zone {v}
              </option>
            ))}
          </select>
        </label>
        <label className="pms-rack-jump">
          <input
            type="search"
            value={filters.search}
            placeholder="N°, vue, remarque…"
            aria-label="Rechercher un logement"
            onChange={(e) => setFilter({ search: e.target.value })}
          />
        </label>

        {/* Only while the window shown is not yet read: a refresh of a window
            already covered is silent, since nothing on screen is unknown. */}
        {windowUnread && (
          <span className="pms-rack-reading" role="status">
            Lecture de la période…
          </span>
        )}

        {/* A filter on an OCCUPANCY plan hides units, and a hidden unit is one
            you can double-book without seeing it. Configuration can afford a
            quiet filter; this cannot, so the count is permanent and shouts. */}
        <span className={`pms-rack-count ${hidden > 0 ? 'is-filtered' : ''}`}>
          {rows.length}/{allRows.length}
          {hidden > 0 && (
            <>
              {' '}
              — {hidden} masqué{hidden > 1 ? 's' : ''}
              <button type="button" className="pms-chip" onClick={() => setFilters(EMPTY_ROOM_CONFIG_FILTERS)}>
                Tout afficher
              </button>
            </>
          )}
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {readErr && <p className="auth-err">Erreur : {readErr}</p>}

      <div className="pms-rack-split">
        <div className="pms-rack-wrap" ref={wrapRef}>
          <div
            className="pms-rack"
            style={{ ['--rack-days' as string]: String(days.length) }}
          >
            {/* The month band carries what the day caption no longer can. « mer.
                12/08 » in a 25-column grid collided with its neighbours (operator
                screenshot); « mer 12 » fits, and the month is stated once per run
                above it — which is also where Vega puts it. */}
            <div className="pms-rack-row is-months">
              {/* The origin key, in the one cell the grid never uses. */}
              <span className="pms-rack-label pms-rack-legend">
                <span className="pms-rack-legend-k">Origine</span>
                {PMS_BOOKING_ORIGINS.map((o) => (
                  <span key={o} className={`pms-rack-origin is-${o}`} title={PMS_BOOKING_ORIGIN_LABELS_FR[o]}>
                    {PMS_BOOKING_ORIGIN_LETTERS[o]}
                  </span>
                ))}
              </span>
              {rackMonthBands(days).map((b, i, all) => {
                const start = all.slice(0, i).reduce((n, x) => n + x.span, 0);
                return (
                  <span key={b.label} className="pms-rack-month" style={{ gridColumn: `${start + 2} / span ${b.span}` }}>
                    {b.label}
                  </span>
                );
              })}
            </div>

            <div className="pms-rack-row is-head">
              <RackStripHead
                sortKey={preset === null ? sortKey : null}
                sortDir={sortDir}
                onSort={toggleSort}
              />
              {days.map((d, i) => (
                <span
                  key={d}
                  className={`pms-rack-day ${d === todayIso ? 'is-today' : ''} ${isWeekend(d) ? 'is-weekend' : ''} ${
                    unread(d) ? 'is-unread' : ''
                  }`}
                  style={{ gridColumn: i + 2 }}
                >
                  {fmtDayCompact(d)}
                </span>
              ))}
            </div>

            {!multi && seasonRow(seasonCals, 'season')}

            {reservations === null && <p className="pms-empty">Chargement…</p>}

            {multi ? (
              sections.map((sec) => (
                <Fragment key={sec.code}>
                  <div className="pms-rack-group is-etab" data-testid="rack-section">
                    {sec.label}
                  </div>
                  {seasonRow(sec.seasonCals, `season-${sec.code}`)}
                  {unassignedBand(sec.unassigned)}
                  {sec.rows.map(unitRow)}
                </Fragment>
              ))
            ) : (
              <>
                {unassignedBand(unassigned)}
                {/* FLAT — no category bands (see the density note above). The order
                    is whatever the preset or the column click says, « N° de chambre »
                    by default. */}
                {rows.map(unitRow)}
              </>
            )}

            {reservations !== null && allRows.length === 0 && (
              <p className="pms-empty">{multi ? 'Aucun logement.' : 'Aucun logement dans cet établissement.'}</p>
            )}
            {reservations !== null && allRows.length > 0 && rows.length === 0 && (
              <p className="pms-empty">Aucun logement ne correspond aux filtres.</p>
            )}
          </div>
        </div>

        {selection !== null && (
          <RackInspector
            selection={selection}
            money={money}
            advance={advance}
            planGap={planGap}
            onOpen={openFromSelection()}
            onClose={() => setPicked(null)}
          />
        )}
      </div>

      {/* An OVERLAY, not a card in the flow: rendered above the grid it pushed
          42 rows down, so every booking started by losing the view that started
          it. A click on the backdrop closes it ONLY when nothing was changed;
          otherwise it asks first — there is typed work inside. */}
      {form && (
        <div
          className="pms-overlay"
          onMouseDown={(e) => {
            pressedBackdrop.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && pressedBackdrop.current) closeForm();
            pressedBackdrop.current = false;
          }}
        >
          <div className="pms-overlay-panel">
            <ReservationForm
              initial={form}
              roomTypes={roomTypes}
              ratePlans={ratePlans}
              guests={pms.guests}
              rooms={rooms}
              products={products}
              saving={saving}
              error={err}
              onSave={(f: FormState, code: string | null) => void save(f, code)}
              propertyCode={formProperty}
              etablissementLabels={etablissementLabels}
              onCancel={closeForm}
              onDirtyChange={setFormDirty}
              advance={formAdvance}
              planGap={formPlanGap}
              billed={formBilled}
              journal={formJournal}
              canSeeNeeds={canNeeds}
              vegaHeld={formHeld}
            />
          </div>
        </div>
      )}
    </div>
  );
}
